import { useState, useEffect, useId, type ReactNode } from "react";
import { ChevronDown } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import MobileHeader from "../layout/MobileHeader";
import { MobileContent } from "../layout/MobileContent";
import { MobileBackButton } from "../misc/MobileBackButton";
/* eslint-disable react-refresh/only-export-components */
import { EditBase, Form, useDataProvider, useNotify } from "ra-core";
import type { RaRecord } from "ra-core";
import { useFormContext } from "react-hook-form";
import { TextInput } from "@/components/admin/text-input";
import { ArrayInput } from "@/components/admin/array-input";
import { SimpleFormIterator } from "@/components/admin/simple-form-iterator";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { toSlug } from "@/lib/toSlug";
import {
  useConfigurationContext,
  useConfigurationUpdater,
} from "../root/ConfigurationContext";
import { useDealPipelines } from "../deals/useDealPipelines";
import { FontPreference } from "../root/FontPreference";
import { MotionPreference } from "../root/MotionPreference";
import { PipelineEditor } from "./PipelineEditor";
import { validateQuoteMilestones } from "../specifications/quoteWorkflow";
import { SettingsTags } from "./SettingsTags";
type ValidateItemsInUseMessages = {
  duplicate?: (displayName: string, duplicates: string[]) => string;
  inUse?: (displayName: string, inUse: string[]) => string;
  validating?: string;
};

/**
 * Validate that no items were removed if they are still referenced by existing deals.
 * Also rejects duplicate slug values.
 * Returns undefined if valid, or an error message string.
 */
export const validateItemsInUse = (
  items: { value: string; label: string }[] | undefined,
  deals: RaRecord[] | undefined,
  fieldName: string,
  displayName: string,
  messages?: ValidateItemsInUseMessages,
) => {
  if (!items) return undefined;
  // Check for duplicate slugs
  const slugs = items.map((i) => i.value || toSlug(i.label));
  const seen = new Set<string>();
  const duplicates = new Set<string>();
  for (const slug of slugs) {
    if (seen.has(slug)) duplicates.add(slug);
    seen.add(slug);
  }
  if (duplicates.size > 0) {
    const duplicatesList = [...duplicates];
    return (
      messages?.duplicate?.(displayName, duplicatesList) ??
      `Duplicate ${displayName}: ${duplicatesList.join(", ")}`
    );
  }
  // Check that no in-use value was removed (skip if deals haven't loaded)
  if (!deals) return messages?.validating ?? "Validating…";
  const values = new Set(slugs);
  const inUse = [
    ...new Set(
      deals
        .filter(
          (deal) => deal[fieldName] && !values.has(deal[fieldName] as string),
        )
        .map((deal) => deal[fieldName] as string),
    ),
  ];
  if (inUse.length > 0) {
    return (
      messages?.inUse?.(displayName, inUse) ??
      `Cannot remove ${displayName} that are still used by deals: ${inUse.join(", ")}`
    );
  }
  return undefined;
};

export function SettingsPage() {
  const config = useConfigurationContext();
  const { dealPipelines } = useDealPipelines();
  const update = useConfigurationUpdater();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [validationError, setValidationError] = useState("");
  const prepareConfiguration = async (data: typeof config) => {
    const pipelines = data.dealPipelines ?? [];
    pipelines.forEach(validateQuoteMilestones);
    if (
      !pipelines.length ||
      pipelines.some(
        (p) =>
          !p.label.trim() ||
          !p.stages.length ||
          p.stages.some((s) => !s.label.trim()),
      )
    )
      throw new Error(
        "Заполните названия воронок и этапов. В каждой воронке нужен хотя бы один этап.",
      );
    for (const previous of dealPipelines) {
      const next = pipelines.find((p) => p.value === previous.value);
      const removed = previous.stages.filter(
        (stage) => !next?.stages.some((s) => s.value === stage.value),
      );
      for (const stage of removed) {
        const { total, data: used } = await dataProvider.getList("deals", {
          pagination: { page: 1, perPage: 1 },
          sort: { field: "id", order: "ASC" },
          filter: {
            pipeline: previous.value,
            ...(!next ? {} : { stage: stage.value }),
          },
        });
        if ((total ?? used.length) > 0)
          throw new Error(
            `Воронка «${previous.label}», этап «${stage.label}» используются. Сначала перенесите сделки в другой этап или воронку.`,
          );
        if (!next) break;
      }
    }
    return {
      config: {
        ...config,
        ...data,
        dealPipelines: pipelines,
        companySectors: data.companySectors.map((item) => ({
          ...item,
          value: item.value || toSlug(item.label),
        })),
        taskTypes: data.taskTypes.map((item) => ({
          ...item,
          value: item.value || toSlug(item.label),
        })),
      },
    };
  };
  const mobile = useIsMobile();
  const form = (
    <EditBase
      resource="configuration"
      id={1}
      redirect={false}
      mutationMode="pessimistic"
      transform={(data) => ({
        config: {
          ...config,
          ...data,
          companySectors: data.companySectors.map(
            (item: { value?: string; label: string }) => ({
              ...item,
              value: item.value || toSlug(item.label),
            }),
          ),
          taskTypes: data.taskTypes.map(
            (item: { value?: string; label: string }) => ({
              ...item,
              value: item.value || toSlug(item.label),
            }),
          ),
        },
      })}
      mutationOptions={{
        onSuccess: (data) => {
          update(data.config);
          notify("Настройки сохранены");
        },
        onError: (error: Error) =>
          notify(error.message || "Не удалось сохранить настройки", {
            type: "error",
          }),
      }}
    >
      <Form
        record={{ ...config, dealPipelines }}
        defaultValues={{ ...config, dealPipelines }}
        validate={async (data) => {
          try {
            await prepareConfiguration(data as typeof config);
            setValidationError("");
            return {};
          } catch (error) {
            const message = (error as Error).message;
            setValidationError(message);
            return { dealPipelines: message };
          }
        }}
      >
        {validationError && (
          <p
            role="alert"
            className="rounded-xl border border-destructive text-destructive p-4 mt-4"
          >
            {validationError}
          </p>
        )}
        <SettingsFields clearError={() => setValidationError("")} />
      </Form>
    </EditBase>
  );
  return mobile ? (
    <>
      <MobileHeader>
        <MobileBackButton to="/settings" />
        <h1 className="font-semibold">Настройки CRM</h1>
      </MobileHeader>
      <MobileContent>{form}</MobileContent>
    </>
  ) : (
    form
  );
}
SettingsPage.path = "/settings";
function SettingsFields({ clearError }: { clearError: () => void }) {
  const config = useConfigurationContext();
  const { dealPipelines } = useDealPipelines();
  const {
    reset,
    formState: { isSubmitting },
  } = useFormContext();
  return (
    <div
      data-settings-page
      className="max-w-4xl mx-auto py-2 md:py-6 pb-8 space-y-4"
    >
      <h1 className="hidden md:block text-2xl font-semibold">Настройки CRM</h1>
      <SettingsSection title="Воронки и этапы сделок" field="dealPipelines">
        <PipelineEditor />
      </SettingsSection>
      <SettingsSection title="Оформление" field="appearance">
        <FontPreference />
        <MotionPreference />
      </SettingsSection>
      <SettingsSection title="Направления компаний" field="companySectors">
        <ArrayInput source="companySectors" label={false}>
          <SimpleFormIterator disableClear>
            <TextInput source="label" label="Название" />
          </SimpleFormIterator>
        </ArrayInput>
      </SettingsSection>
      <SettingsSection title="Типы задач" field="taskTypes">
        <ArrayInput source="taskTypes" label={false}>
          <SimpleFormIterator disableClear>
            <TextInput source="label" label="Название" />
          </SimpleFormIterator>
        </ArrayInput>
      </SettingsSection>
      <SettingsSection title="Теги" field="tags">
        <SettingsTags />
      </SettingsSection>
      <div
        data-settings-save
        className="sticky z-20 md:fixed md:bottom-0 md:inset-x-0 rounded-3xl md:rounded-none bg-background p-3 flex flex-wrap justify-end gap-3"
      >
        <Button
          type="button"
          variant="outline"
          onClick={() => {
            reset({ ...config, dealPipelines });
            clearError();
          }}
        >
          Отменить
        </Button>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? "Сохранение…" : "Сохранить настройки"}
        </Button>
      </div>
    </div>
  );
}

function SettingsSection({
  title,
  field,
  children,
}: {
  title: string;
  field: string;
  children: ReactNode;
}) {
  const mobile = useIsMobile();
  const [expanded, setExpanded] = useState(false);
  const id = useId();
  const {
    formState: { errors },
  } = useFormContext();
  const hasError = !!errors[field];
  useEffect(() => {
    if (hasError) setExpanded(true);
  }, [hasError]);
  const open = !mobile || expanded;
  return (
    <Card data-settings-section={field} className="gap-0 py-0 overflow-hidden">
      <h2 className="text-base md:text-xl font-semibold">
        {mobile ? (
          <button
            type="button"
            className="flex w-full items-center justify-between gap-3 p-4 text-left"
            aria-expanded={open}
            aria-controls={id}
            onClick={() => setExpanded((value) => !value)}
          >
            <span>{title}</span>
            <ChevronDown
              className={`size-4 shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
            />
          </button>
        ) : (
          <span className="block p-5 pb-2">{title}</span>
        )}
      </h2>
      <div
        id={id}
        data-mobile-collapse
        data-state={open ? "open" : "closed"}
        inert={!open}
        aria-hidden={!open}
      >
        <div>
          <CardContent className="space-y-4 px-4 pb-4 md:px-5 md:pb-5">
            {children}
          </CardContent>
        </div>
      </div>
    </Card>
  );
}
